import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Lock,
  PlayCircle,
  Clock,
  CheckCircle2,
  MessageCircle,
  ChevronRight,
  ChevronLeft,
  Sparkles,
} from 'lucide-react';
import { courseInfo, courseModules, profile } from '../mock';

const STORAGE_KEY = 'kn_curso_aula_unlocked';
const PROGRESS_KEY = 'kn_curso_aula_progress';

const PasswordGate = ({ onUnlock }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const tryUnlock = (e) => {
    e.preventDefault();
    if (value.trim().toLowerCase() === courseInfo.classroomPassword.toLowerCase()) {
      localStorage.setItem(STORAGE_KEY, '1');
      onUnlock();
    } else {
      setError('Contraseña incorrecta. Revisá el mensaje que te envié por WhatsApp.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F4EDE2] grid place-items-center px-5 py-10">
      <div className="max-w-[460px] w-full bg-[#FAF4E8] border border-[#DBCEB4] rounded-3xl p-9 shadow-[0_30px_60px_-30px_rgba(74,46,32,0.4)]">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-12 h-12 rounded-full bg-[#E8C9C0] grid place-items-center">
            <Lock size={22} strokeWidth={1.6} className="text-[#B8806E]" />
          </span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#8A7E6E]">
            Aula privada · alumnas
          </span>
        </div>
        <h1 className="font-serif text-[30px] leading-[1.05] text-[#2E2A23]">
          Bienvenida al
          <span className="italic text-[#B8806E]"> curso</span>
        </h1>
        <p className="mt-4 text-[14.5px] leading-[1.7] text-[#2E2A23]/75">
          Ingresá la contraseña que te envié por WhatsApp luego de confirmar tu pago.
        </p>

        <form onSubmit={tryUnlock} className="mt-7" data-testid="curso-aula-gate-form">
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#8A7E6E] mb-2">
            Contraseña
          </label>
          <input
            type="password"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError('');
            }}
            className="w-full bg-white border border-[#C5B496] rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-[#B8806E] transition-colors"
            placeholder="••••••••"
            autoFocus
            data-testid="curso-aula-password-input"
          />
          {error && (
            <p className="mt-3 text-[13px] text-[#9A6555]" data-testid="curso-aula-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="btn-primary mt-5 w-full justify-center"
            data-testid="curso-aula-unlock-btn"
          >
            <Sparkles size={15} strokeWidth={1.7} />
            Entrar al aula
          </button>
        </form>

        <div className="mt-7 pt-6 border-t border-[#DBCEB4] text-center">
          <a
            href={`${profile.whatsapp}?text=Hola%20Karina!%20Necesito%20la%20contrase%C3%B1a%20del%20aula%20del%20curso.`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-[13px] text-[#B8806E] hover:text-[#9A6555] transition-colors"
            data-testid="curso-aula-help-link"
          >
            <MessageCircle size={15} strokeWidth={1.7} />
            No tengo la contraseña aún
          </a>
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/curso"
            className="inline-flex items-center gap-1.5 text-[11.5px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E] transition-colors"
          >
            <ArrowLeft size={13} strokeWidth={1.7} />
            Volver al curso
          </Link>
        </div>
      </div>
    </div>
  );
};

const VideoEmbed = ({ url }) => {
  if (!url) {
    return (
      <div className="aspect-video w-full rounded-2xl bg-[#E8DDC8] border border-[#C5B496] grid place-items-center text-[#8A7E6E]">
        <div className="text-center px-6">
          <PlayCircle size={42} strokeWidth={1.3} className="text-[#B8806E] mx-auto mb-3" />
          <p className="font-serif italic text-[20px] text-[#2E2A23]">
            Video disponible próximamente
          </p>
          <p className="text-[13px] mt-2">
            Estoy terminando de grabar este módulo. Te aviso por WhatsApp cuando esté
            listo.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="aspect-video w-full rounded-2xl overflow-hidden border border-[#DBCEB4] shadow-[0_20px_40px_-25px_rgba(74,46,32,0.4)]">
      <iframe
        src={url}
        title="Curso de lactancia"
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

const CursoAula = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [activeId, setActiveId] = useState(courseModules[0].id);
  const [completed, setCompleted] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(PROGRESS_KEY) || '{}');
    } catch {
      return {};
    }
  });

  useEffect(() => {
    setUnlocked(localStorage.getItem(STORAGE_KEY) === '1');
  }, []);

  useEffect(() => {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(completed));
  }, [completed]);

  const activeModule = useMemo(
    () => courseModules.find((m) => m.id === activeId) || courseModules[0],
    [activeId]
  );

  const idx = courseModules.findIndex((m) => m.id === activeId);
  const prev = idx > 0 ? courseModules[idx - 1] : null;
  const next = idx < courseModules.length - 1 ? courseModules[idx + 1] : null;

  const toggleComplete = () => {
    setCompleted((c) => ({ ...c, [activeId]: !c[activeId] }));
  };

  const completedCount = Object.values(completed).filter(Boolean).length;
  const progressPct = Math.round((completedCount / courseModules.length) * 100);

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#F4EDE2] text-[#2E2A23]">
      {/* header */}
      <header className="sticky top-0 z-30 bg-[#F4EDE2]/92 backdrop-blur-md border-b border-[#DBCEB4]">
        <div className="max-w-[1300px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="curso-aula-back">
            <span className="w-8 h-8 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center font-serif text-base leading-none">
              k
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[16px] text-[#2E2A23]">Aula del curso</span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#8A7E6E]">
                Karina Negrin
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-5">
            <div className="hidden sm:flex items-center gap-3" data-testid="curso-aula-progress">
              <div className="w-32 h-1.5 rounded-full bg-[#E8DDC8] overflow-hidden">
                <div
                  className="h-full bg-[#B8806E] transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="text-[11.5px] tracking-[0.18em] uppercase text-[#8A7E6E]">
                {completedCount}/{courseModules.length}
              </span>
            </div>
            <a
              href={profile.whatsapp}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E] transition-colors"
              data-testid="curso-aula-help"
            >
              <MessageCircle size={14} strokeWidth={1.7} />
              <span className="hidden sm:inline">Ayuda</span>
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-5 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-[300px,1fr] gap-10">
        {/* sidebar */}
        <aside className="lg:sticky lg:top-24 self-start" data-testid="curso-aula-sidebar">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-4">
            Módulos
          </div>
          <nav className="space-y-1.5">
            {courseModules.map((m) => {
              const isActive = m.id === activeId;
              const isDone = !!completed[m.id];
              return (
                <button
                  key={m.id}
                  onClick={() => setActiveId(m.id)}
                  className={`w-full text-left rounded-xl px-4 py-3 border transition-colors flex items-start gap-3 ${
                    isActive
                      ? 'bg-[#FAF4E8] border-[#B8806E]'
                      : 'bg-transparent border-[#DBCEB4]/70 hover:bg-[#FAF4E8]'
                  }`}
                  data-testid={`curso-aula-module-btn-${m.id}`}
                >
                  <span
                    className={`flex-none w-7 h-7 rounded-full grid place-items-center font-serif text-[13px] mt-0.5 ${
                      isDone
                        ? 'bg-[#8FA08A] text-white'
                        : isActive
                          ? 'bg-[#B8806E] text-white'
                          : 'bg-[#E8DDC8] text-[#B8806E]'
                    }`}
                  >
                    {isDone ? <CheckCircle2 size={14} strokeWidth={2} /> : m.id}
                  </span>
                  <span className="flex-1">
                    <span className="block text-[13.5px] leading-[1.35] text-[#2E2A23]">
                      {m.name}
                    </span>
                    <span className="text-[10.5px] tracking-[0.18em] uppercase text-[#8A7E6E] mt-1 inline-block">
                      {m.duration}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* main */}
        <main>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-2">
            Módulo {String(activeModule.id).padStart(2, '0')} ·{' '}
            <span className="inline-flex items-center gap-1">
              <Clock size={11} strokeWidth={1.8} /> {activeModule.duration}
            </span>
          </div>
          <h1
            className="font-serif text-[34px] lg:text-[44px] leading-[1.06] tracking-[-0.015em] text-[#2E2A23]"
            data-testid="curso-aula-module-title"
          >
            {activeModule.name}
          </h1>
          <p className="mt-4 text-[15.5px] leading-[1.75] text-[#2E2A23]/80 font-light max-w-[720px]">
            {activeModule.summary}
          </p>

          <div className="mt-8" data-testid="curso-aula-video">
            <VideoEmbed url={activeModule.videoUrl} />
          </div>

          {/* completion */}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              onClick={toggleComplete}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full border text-[12.5px] tracking-[0.14em] uppercase transition-colors ${
                completed[activeId]
                  ? 'bg-[#8FA08A] border-[#8FA08A] text-white'
                  : 'bg-transparent border-[#C5B496] text-[#2E2A23] hover:border-[#B8806E] hover:text-[#B8806E]'
              }`}
              data-testid="curso-aula-mark-complete"
            >
              <CheckCircle2 size={15} strokeWidth={1.8} />
              {completed[activeId] ? 'Visto · sigamos' : 'Marcar como visto'}
            </button>
          </div>

          {/* body content */}
          <article
            className="mt-10 prose-curso max-w-[760px]"
            data-testid="curso-aula-body"
            dangerouslySetInnerHTML={{ __html: activeModule.bodyHtml }}
          />

          {/* nav prev/next */}
          <div className="mt-14 grid grid-cols-2 gap-4 max-w-[760px]">
            <button
              onClick={() => prev && setActiveId(prev.id)}
              disabled={!prev}
              className="flex items-center gap-3 rounded-xl border border-[#DBCEB4] bg-[#FAF4E8] px-5 py-4 text-left disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white transition-colors"
              data-testid="curso-aula-prev"
            >
              <ChevronLeft size={20} strokeWidth={1.6} className="text-[#B8806E] flex-none" />
              <span>
                <span className="block text-[10.5px] tracking-[0.22em] uppercase text-[#8A7E6E]">
                  Anterior
                </span>
                <span className="block text-[13px] text-[#2E2A23] mt-0.5 leading-tight">
                  {prev ? prev.name : 'Estás en el primer módulo'}
                </span>
              </span>
            </button>
            <button
              onClick={() => next && setActiveId(next.id)}
              disabled={!next}
              className="flex items-center gap-3 rounded-xl border border-[#DBCEB4] bg-[#FAF4E8] px-5 py-4 text-right disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white transition-colors justify-end"
              data-testid="curso-aula-next"
            >
              <span>
                <span className="block text-[10.5px] tracking-[0.22em] uppercase text-[#8A7E6E]">
                  Siguiente
                </span>
                <span className="block text-[13px] text-[#2E2A23] mt-0.5 leading-tight">
                  {next ? next.name : '¡Llegaste al final!'}
                </span>
              </span>
              <ChevronRight size={20} strokeWidth={1.6} className="text-[#B8806E] flex-none" />
            </button>
          </div>

          <div className="mt-16 pt-8 border-t border-[#DBCEB4] text-center">
            <p className="font-serif italic text-[20px] text-[#2E2A23]/80">
              ¿Te quedó alguna duda de este módulo?
            </p>
            <a
              href={`${profile.whatsapp}?text=Hola%20Karina!%20Tengo%20una%20duda%20del%20m%C3%B3dulo%20"${encodeURIComponent(activeModule.name)}"%20del%20curso.`}
              target="_blank"
              rel="noreferrer"
              className="btn-primary mt-5 inline-flex"
              data-testid="curso-aula-question-cta"
            >
              <MessageCircle size={15} strokeWidth={1.7} />
              Escribirle a Karina
            </a>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CursoAula;
